import React from "react";

import moment from "moment";

import { getTimezoneName } from "../getTimezoneName";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isEmpty = (val: any): val is undefined | null => {
  return typeof val === "undefined" || val === null;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isString = (val: any): val is string => {
  return typeof val === "string";
};

// for dates from DateInput component (dd/mm/yyyy)
export const formatDateInput = (str: string | typeof undefined | null): string => {
  if (str) {
    const splitArr = str?.split("/");
    const year = splitArr?.pop();

    year && splitArr.unshift(year);

    return splitArr && splitArr.join("-");
  } else {
    return "";
  }
};

type DateValueType = null | typeof undefined | string | moment.Moment | Date;

export const formatDate = (
  val: DateValueType,
  dateFormatString: string = "YYYY-MM-DD hh:mm:ss A",
  timezone = false,
  appendTimezone = false,
): string => {
  if (isEmpty(val)) {
    return val || "";
  }

  const dateFormat = (v: moment.MomentInput): string => {
    if (timezone) {
      const ret = moment(v).format(dateFormatString);
      if (appendTimezone) {
        return ret + " " + getTimezoneName(new Date((v || "").toString()));
      } else {
        return ret;
      }
    } else {
      const ret = moment.utc(v).format(dateFormatString);
      if (appendTimezone) {
        return ret + " UTC";
      } else {
        return ret;
      }
    }
  };

  if (
    isString(val)
    && val.search(
      /^(\d{4})\D?(0[1-9]|1[0-2])\D?([12]\d|0[1-9]|3[01])(\D?([01]\d|2[0-3])\D?([0-5]\d)\D?([0-6]\d([.]\d+)?)?)([zZ]|([+-][01]\d|2[0-3])\D?([0-5]\d)?)?$/,
    ) !== -1
  ) {
    return dateFormat(val);
  }

  // for values that come back formatted as YYYY-MM-DD
  if (isString(val) && val.search(/^(\d{4})\D?(0[1-9]|1[0-2])\D?([12]\d|0[1-9]|3[01])$/) !== -1) {
    return dateFormat(val);
  }

  // for values that come back formatted as MM/DD/YYYY
  if (isString(val) && val.search(/^(0[1-9]|1[0-2])\/([12]\d|0[1-9]|3[01])\/\d{4}$/) !== -1) {
    return dateFormat(val);
  }

  // for values that come back formatted as MM/DD/YYYY hh:mm:ss
  if (
    isString(val)
    && val.search(
      /^(0[1-9]|1[0-2])\/([12]\d|0[1-9]|3[01])\/\d{4}(\D?([01]\d|2[0-3])\D?([0-5]\d)\D?([0-6]\d([.]\d+)?)?)([zZ]|([+-][01]\d|2[0-3])\D?([0-5]\d)?)?$/,
    ) !== -1
  ) {
    return dateFormat(val);
  }

  if (moment.isMoment(val)) {
    return dateFormat(val);
  }

  if (val instanceof Date) {
    return dateFormat(val);
  }

  return val;
};

// Date in local time
export const formatDateLocal = (val: DateValueType, dateFormatString?: string): string => {
  return formatDate(val, dateFormatString, true);
};

export const formatDateUTCWithTimezone = (val: DateValueType, dateFormatString?: string): string => {
  return formatDate(val, dateFormatString, false, true);
};

export const formatDateLocalWithTimezone = (val: DateValueType, dateFormatString?: string): string => {
  return formatDate(val, dateFormatString, true, true);
};

export const splitDateTime = (val: DateValueType, dateFormatString: string = "MM/DD/YYYY", timezone: boolean = false): Array<string> => {
  if (val === null) return [];
  return formatDate(val, dateFormatString, timezone).split(" ");
};

/* Takes an input and validates for NaN. If valid, convert to
   abbreviated human readable format. Else, return input. */
export const formatDollars = (value: number | string): string => {
  let formatValue = value;

  if (value === null) {
    return "";
  }

  if (typeof formatValue === "string" && (isNaN(Number(formatValue)) || isNaN(parseFloat(formatValue)))) {
    return formatValue;
  }

  if (typeof formatValue === "string" || !isNaN(formatValue)) {
    formatValue = typeof formatValue === "string" ? parseFloat(formatValue) : formatValue;
    let negative = false;
    if (formatValue < 0) {
      formatValue = Math.abs(formatValue);
      negative = true;
    }

    // This is a bit odd, we should probably not be switching types and rather make this more explicit.
    let amount: number | string = formatValue;

    if (formatValue < 1e6) amount = formatValue.toFixed(2);
    else if (formatValue >= 1e6 && formatValue < 1e9) amount = (formatValue / 1e6).toFixed(2) + "MM";
    else if (formatValue >= 1e9 && formatValue < 1e12) amount = (formatValue / 1e9).toFixed(2) + "B";
    else if (formatValue >= 1e12) amount = (formatValue / 1e12).toFixed(1) + "T";

    if (negative) {
      return "-$" + amount.toLocaleString();
    }
    return "$" + amount.toLocaleString();
  }
  return formatValue.toLocaleString();
};

/* Takes an input and validates for NaN. If valid, convert to
   abbreviated human readable format with commas. Else, return input. */
export const formatDollarsWithCommas = (value: number | string): string => {
  let formatValue = value;

  if (value === null) {
    return "";
  }

  const finalFormat = (value: string) => {
    const stringValue = String(value);
    const [whole, fraction] = stringValue.split(".");

    return `${(~~whole).toLocaleString()}.${fraction}`;
  };

  if (typeof formatValue === "string" && isNaN(Number(formatValue))) {
    return formatValue;
  }

  if (typeof formatValue === "string" || !isNaN(formatValue)) {
    formatValue = typeof formatValue === "string" ? parseFloat(formatValue) : formatValue;
    let negative = false;
    if (formatValue < 0) {
      formatValue = Math.abs(formatValue);
      negative = true;
    }

    // This is a bit odd, we should probably not be switching types and rather make this more explicit.
    let amount: number | string = formatValue;

    if (formatValue < 1e6) amount = formatValue.toFixed(2);
    else if (formatValue >= 1e6 && formatValue < 1e9) amount = (formatValue / 1e6).toFixed(2) + "MM";
    else if (formatValue >= 1e9 && formatValue < 1e12) amount = (formatValue / 1e9).toFixed(2) + "B";
    else if (formatValue >= 1e12) amount = (formatValue / 1e12).toFixed(1) + "T";

    if (negative) {
      return "-$" + finalFormat(amount as string);
    }
    return "$" + finalFormat(amount as string);
  }
  return finalFormat(String(formatValue));
};

export const formatPercent = (value: number | string, isDecimal: boolean = false): string => {
  let formatValue = value;

  if (value === null || value === undefined) {
    return "";
  }

  if (typeof formatValue === "string" && (isNaN(Number(formatValue)) || isNaN(parseFloat(formatValue)))) {
    return formatValue;
  }

  if (typeof formatValue === "string" || !isNaN(formatValue)) {
    formatValue = typeof formatValue === "string" ? parseFloat(formatValue) : formatValue;
    let negative = false;
    if (formatValue < 0) {
      formatValue = Math.abs(formatValue);
      negative = true;
    }

    if (isDecimal) {
      formatValue = formatValue * 100;
    }

    // This is a bit odd, we should probably not be switching types and rather make this more explicit.
    const amount: number | string = formatValue;

    if (negative) {
      return "-" + amount.toLocaleString() + "%";
    }
    return amount.toLocaleString() + "%";
  }

  return formatValue.toLocaleString();
};

/* Takes a phone number and normalizes any extensions to ` x<digits>` */
export const formatPhoneNumber = (number: string | null | undefined, useParens = false): string => {
  if (!number) return "";

  const extensionMatch = number.trim().match(/^(.*?)(?:\s*(?:x|ext\.?|extension|#|;)\s*(\d+))?\s*$/i);
  const baseNumber = extensionMatch?.[1]?.trim() ?? number;
  const extension = extensionMatch?.[2] ?? "";

  const phone_stripped = baseNumber.replace(/\D/g, "");
  let new_phone = baseNumber;
  if (phone_stripped.length === 10)
    new_phone = useParens
      ? `(${phone_stripped.slice(0, 3)}) ${phone_stripped.slice(3, 6)}-${phone_stripped.slice(6)}`
      : `${phone_stripped.slice(0, 3)}-${phone_stripped.slice(3, 6)}-${phone_stripped.slice(6)}`;
  else if (phone_stripped.length === 11)
    new_phone = useParens
      ? `${phone_stripped.slice(0, 1)} (${phone_stripped.slice(1, 4)}) ${phone_stripped.slice(4, 7)}-${phone_stripped.slice(7)}`
      : `${phone_stripped.slice(0, 1)}-${phone_stripped.slice(1, 4)}-${phone_stripped.slice(4, 7)}-${phone_stripped.slice(7)}`;

  if (extension) {
    return `${new_phone} x${extension}`;
  }

  return new_phone;
};

/* Takes a boolean field and transform it to yes or no */
export const formatBoolean = (value: boolean): string => {
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  return value;
};

/* Takes a timestamp and transforms it to a readable form */
export const formatDateTime = <T>(value: T): string => {
  return moment(String(value)).format("MM/DD/YYYY h:mm:ss a");
};

/* Takes a timestamp and transforms it to a readable AM to PM form */
export const formatTimeAMToPM = (value: string): string => {
  if (value === null || typeof value === undefined || value === undefined) {
    return "";
  }

  if (moment(value).isValid()) {
    return moment(value).format("h:mm:ss a");
  }

  return "";
};

/* Takes in a string and formats it to TitleCase */
export const formatTitleCase = (value: null | typeof undefined | string): string => {
  if (typeof value === "string") {
    return value.replace(/\w\S*/g, (txt: string) => {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  }
  return "";
};

/**
 * Combine recipient address to one string
 * @param businessName
 * @param firstName
 * @param lastName
 * @param addressLine1
 * @param addressLine2
 * @param city
 * @param state
 * @param zip
 */
export const formatAddress = (
  businessName: string,
  firstName: string,
  lastName: string,
  addressLine1: string,
  addressLine2: string,
  city: string,
  state: string,
  zip: string,
): string => {
  const personsName = [firstName, lastName].filter(Boolean).join(" ");
  return [businessName, personsName, addressLine1, addressLine2, `${city}, ${state} ${zip}`].filter(Boolean).join("\n");
};

/* Takes a number and returns with correct ordinal*/
export const formatNumberWithOrdinal = (number: number): string => {
  const suffices = ["th", "st", "nd", "rd"];
  return number + (suffices[(number - 20) % 10] || suffices[number] || suffices[0]);
};

/* Takes zip number and returns after formatting */
export const formatZip = (zip: string): string => {
  if (zip) {
    const updatedZip = zip.replace(/[^0-9]/g, "");
    if (updatedZip?.length === 9) {
      return updatedZip.slice(0, 5) + "-" + updatedZip.slice(5);
    }
    return updatedZip;
  }
  return "";
};

export const formatPhone = (e: React.ChangeEvent<HTMLInputElement>): string => {
  const { value } = e.target;
  if (!value) {
    return value;
  }
  const cleanNumber = value.replace(/[^\d]/g, "");
  const cleanNumberLength = cleanNumber.length;

  if (cleanNumberLength < 4) {
    return cleanNumber;
  }
  if (cleanNumberLength < 7) {
    return `(${cleanNumber.slice(0, 3)}) ${cleanNumber.slice(3)}`;
  }

  return `(${cleanNumber.slice(0, 3)}) ${cleanNumber.slice(3, 6)}-${cleanNumber.slice(6, 10)}`;
};

export const formatPriceDisplay = (price: number): string => {
  return `${new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(price))}`;
};

export const calculateAge = (dateOfBirth: string | null | undefined): string => {
  if (!dateOfBirth) {
    return "";
  }
  const age = moment().diff(moment(dateOfBirth), "years");
  return `${age}`;
};

export const formatRemarkCodesTooltipInformation = (
  source: string | null,
  type: string | null,
  remark_code_description: string | null,
): string => `Source: ${source || "--"}; Type: ${type || "--"}; Description: ${remark_code_description || "--"}`;

export const capitalizeFirstLetter = (inputString: string) => {
  return inputString ? `${inputString.charAt(0).toUpperCase()}${inputString.slice(1)}` : "";
};

export const snakeCaseToTitle = (inputString: string): string => {
  if (!inputString) {
    return "";
  }

  const nameArray = inputString.split("_").filter((value: string) => value.length > 0);
  return nameArray.map(capitalizeFirstLetter).join(" ");
};
